import './App.css';
import React, { useState, useEffect } from 'react';
import { autoDetectFirebase, subscribeToCollection, saveDocument, deleteDocument } from './firebase';

// Import Custom Modular Components
import FlightTrackerV3 from './components/FlightTrackerV3';
import Stay from './components/Stay';
import GroceryList from './components/GroceryList';
import DinnerDishes from './components/DinnerDishes';
import LocalActivities from './components/LocalActivities';
import BookClub from './components/BookClub';
import PollBoard from './components/PollBoard';
import PackingList from './components/PackingList';
import Planning from './components/Planning';
import DiscussionThread from './components/DiscussionThread';
import VoteButton from './components/VoteButton';
import Lightbox from './components/Lightbox';
import { getVacationCountdown } from './utils/dateUtils';

// Editorial line icons (lucide-react) — replaces the old PNG travel stamps
import {
  LayoutDashboard,
  CalendarDays,
  Plane,
  Home,
  ShoppingCart,
  UtensilsCrossed,
  MapPin,
  BookOpen,
  Luggage,
  Sun,
  Moon,
  Lock,
} from 'lucide-react';


// Tab mapping for URL hash routing
const TAB_MAP = {
  dashboard: "Dashboard",
  schedule: "Itinerary",
  planning: "Itinerary",
  flights: "Flights",
  stay: "Stay",
  rooms: "Stay", // legacy link — Rooms folded into the Stay tab
  cars: "Stay", // legacy link — Cars folded into the Stay tab
  grocery: "Grocery",
  dinners: "Dinners",
  activities: "Activities",
  bookclub: "BookClub",
  packing: "Packing"
};

function App() {
  const [password, setPassword] = useState("");
  const [userNameInput, setUserNameInput] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [authError, setAuthError] = useState("");


  // Real-time Users from Firestore
  const [users, setUsers] = useState([]);

  // Live subscription & auto-seeding of users
  useEffect(() => {
    const unsubscribe = subscribeToCollection("users", (data) => {
      if (data.length === 0) {
        // Prepopulate users collection with default friends
        const INITIAL_FAM_NAMES = [
          "Aleyda", "Azayza", "Caroline", "Hailey", "Henry", "Janet", "Tedra", "Wynn"
        ];
        INITIAL_FAM_NAMES.forEach(async (name, idx) => {
          const u = { id: String(idx + 1), name };
          await saveDocument("users", u.id, u);
        });
      } else {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setUsers(sorted);
      }
    });
    return () => unsubscribe();
  }, []);



  // Sync hash to activeTab on load / login / hashchange
  useEffect(() => {
    if (!isAuthorized) return;

    const handleHashChange = () => {
      let hash = window.location.hash.slice(1).toLowerCase();
      if (hash.includes('?')) {
        hash = hash.split('?')[0];
      }
      if (TAB_MAP[hash]) {
        setActiveTab(TAB_MAP[hash]);
      }
    };

    handleHashChange(); // Run once when authorized to pick up initial link

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [isAuthorized]);

  // Sync activeTab to hash
  useEffect(() => {
    if (!isAuthorized) return;
    let currentHash = window.location.hash.slice(1).toLowerCase();
    if (currentHash.includes('?')) {
      currentHash = currentHash.split('?')[0];
    }
    const targetHash = activeTab.toLowerCase();
    if (currentHash !== targetHash) {
      window.location.hash = targetHash;
    }
  }, [activeTab, isAuthorized]);

  // Scroll active tab into view in mobile scrollable navbar
  useEffect(() => {
    if (!isAuthorized) return;
    const timer = setTimeout(() => {
      const activeEl = document.querySelector('.sidebar .nav-item.active');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, isAuthorized]);

  const nameInputRef = React.useRef(null);
  const passwordInputRef = React.useRef(null);

  // Light/Dark Theme management (Default to light mode)
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("dayones_vacation_theme") || "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("dayones_vacation_theme", theme);
    // Keep Safari's status bar / toolbar tint (see the theme-color meta tag
    // in public/index.html) matching whichever trip photo is showing —
    // that meta tag doesn't follow data-theme on its own since our theme
    // is an in-app toggle, not the system color scheme.
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute("content", theme === "dark" ? "#1a1a1a" : "#A8C9DE");
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  // Auto-detect Firebase environment details dynamically on load
  useEffect(() => {
    autoDetectFirebase();
  }, []);

  // Persist session & load saved name
  useEffect(() => {
    const savedAuth = localStorage.getItem("dayones_vacation_auth");
    if (savedAuth === "true") {
      setIsAuthorized(true);
    }
    const savedName = localStorage.getItem("dayones_user_name");
    if (savedName) {
      setUserNameInput(savedName);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthorized(false);
    setPassword("");
    setUserNameInput("");
    localStorage.removeItem("dayones_vacation_auth");
    localStorage.removeItem("dayones_user_name");
  };

  // Autofocus the correct input field on password gate mount
  useEffect(() => {
    if (!isAuthorized) {
      const timer = setTimeout(() => {
        const savedName = localStorage.getItem("dayones_user_name");
        if (savedName && passwordInputRef.current) {
          passwordInputRef.current.focus();
        } else if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthorized]);

  // Handle focus recovery when window gains focus
  useEffect(() => {
    const handleWindowFocus = () => {
      if (!isAuthorized) {
        const savedName = localStorage.getItem("dayones_user_name");
        if (savedName && passwordInputRef.current) {
          passwordInputRef.current.focus();
        } else if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [isAuthorized]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    const matchedUser = users.find(u => String(u.id) === String(userNameInput));
    if (!matchedUser) {
      setAuthError("Please select your name.");
      return;
    }

    if (password.toLowerCase() === "sunshine") {
      setIsAuthorized(true);
      setAuthError("");
      localStorage.setItem("dayones_vacation_auth", "true");
      localStorage.setItem("dayones_user_name", matchedUser.id);
      setUserNameInput(matchedUser.id);
    } else {
      setAuthError("Incorrect password.");
    }
  };


  if (!isAuthorized) {
    return (
      <div className="password-gate">
        <div className="password-card" style={{ position: 'relative' }}>
          <button
            type="button"
            className="theme-toggle-btn-gate"
            onClick={toggleTheme}
            title="Toggle Light/Dark Mode"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              transition: 'all 0.2s',
              zIndex: 10
            }}
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          <span className="password-logo">{theme === "light" ? "☀️" : "🌙"}</span>
          <h1>Day Ones Vacation</h1>
          <p className="password-subtitle">
            Welcome to the friend vacation portal.<br />
            Please enter the password to access the website.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="password-input-group" style={{ marginBottom: '16px' }}>
              <select
                ref={nameInputRef}
                className="name-input"
                style={{
                  fontFamily: 'inherit',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                value={userNameInput}
                onChange={e => setUserNameInput(e.target.value)}
                required
              >
                <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select Your Name --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="password-input-group">
              <input
                type="password"
                ref={passwordInputRef}
                className="password-input"
                placeholder="********"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="password-btn">
              Unlock Portal 🔑
            </button>
            {authError && <div className="password-error">{authError}</div>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <button
        className="theme-toggle-btn"
        onClick={toggleTheme}
        title="Toggle Light/Dark Mode"
      >
        {theme === "light" ? (
          <Moon size={20} strokeWidth={2} color="var(--color-primary)" />
        ) : (
          <Sun size={20} strokeWidth={2} fill="currentColor" fillOpacity={0.15} />
        )}
      </button>

      {/* Main Container Layout */}
      <div className="app-container">
        {/* On mobile the page now scrolls at the document level (so iOS
            Safari's toolbar can auto-collapse) instead of being trapped
            inside a fixed-height .app-container, which can now end up much
            taller than one screen. Deliberately NOT sizing this to that
            content height — background-size:cover would then scale
            differently per tab (each tab has different content height, so
            the photo would appear zoomed to a different degree on each
            one). Instead this is pinned to the viewport (position:fixed) at
            a constant one-screen size using the "100lvh" large-viewport-
            height unit, which stays fixed at the toolbar-collapsed size
            instead of live-tracking Safari's toolbar animation (that
            live-tracking is what caused a visible jitter/shrink right after
            load) — content simply scrolls over it. See the
            "@media (max-width: 1024px)" rules for .app-background /
            .app-container in App.css. */}
        <div className="app-background" aria-hidden="true" />
        <div className="app-main-layout">
          {/* Sidebar Router Navigation */}
          <aside className="sidebar">
            <div className="sidebar-brand">
              <div className="sidebar-brand-text">
                <h2>Day Ones</h2>
              </div>
            </div>
            <div className="nav-items-group">
              <button className={`nav-item ${activeTab === "Dashboard" ? "active" : ""}`} onClick={() => setActiveTab("Dashboard")}>
                <span className="nav-item-icon">
                  <LayoutDashboard size={18} strokeWidth={1.75} />
                </span>
                Dashboard
              </button>
              <button className={`nav-item ${activeTab === "Itinerary" ? "active" : ""}`} onClick={() => setActiveTab("Itinerary")}>
                <span className="nav-item-icon">
                  <CalendarDays size={18} strokeWidth={1.75} />
                </span>
                Itinerary
              </button>
              <button className={`nav-item ${activeTab === "Flights" ? "active" : ""}`} onClick={() => setActiveTab("Flights")}>
                <span className="nav-item-icon">
                  <Plane size={18} strokeWidth={1.75} />
                </span>
                Flights
              </button>
              <button className={`nav-item ${activeTab === "Stay" ? "active" : ""}`} onClick={() => setActiveTab("Stay")}>
                <span className="nav-item-icon">
                  <Home size={18} strokeWidth={1.75} />
                </span>
                Lodging
              </button>
              <button className={`nav-item ${activeTab === "Grocery" ? "active" : ""}`} onClick={() => setActiveTab("Grocery")}>
                <span className="nav-item-icon">
                  <ShoppingCart size={18} strokeWidth={1.75} />
                </span>
                Grocery
              </button>
              <button className={`nav-item ${activeTab === "Dinners" ? "active" : ""}`} onClick={() => setActiveTab("Dinners")}>
                <span className="nav-item-icon">
                  <UtensilsCrossed size={18} strokeWidth={1.75} />
                </span>
                Dinner
              </button>
              <button className={`nav-item ${activeTab === "Activities" ? "active" : ""}`} onClick={() => setActiveTab("Activities")}>
                <span className="nav-item-icon">
                  <MapPin size={18} strokeWidth={1.75} />
                </span>
                Activities
              </button>
              <button className={`nav-item ${activeTab === "BookClub" ? "active" : ""}`} onClick={() => setActiveTab("BookClub")}>
                <span className="nav-item-icon">
                  <BookOpen size={18} strokeWidth={1.75} />
                </span>
                Book Club
              </button>
              <button className={`nav-item ${activeTab === "Packing" ? "active" : ""}`} onClick={() => setActiveTab("Packing")}>
                <span className="nav-item-icon">
                  <Luggage size={18} strokeWidth={1.75} />
                </span>
                Packing List
              </button>
            </div>
          </aside>

          {/* Right Console Pane */}
          <div className="console-right">
            {/* Content Render Area */}
            <main className="content-area">
              {activeTab === "Dashboard" && <DashboardView setActiveTab={setActiveTab} users={users} onLogout={handleLogout} />}
              {activeTab === "Itinerary" && <Planning users={users} />}
              {activeTab === "Flights" && <FlightTrackerV3 users={users} />}
              {activeTab === "Stay" && <Stay users={users} />}
              {activeTab === "Grocery" && <GroceryList users={users} />}
              {activeTab === "Dinners" && <DinnerDishes users={users} />}
              {activeTab === "Activities" && <LocalActivities users={users} />}
              {activeTab === "BookClub" && <BookClub users={users} />}
              {activeTab === "Packing" && <PackingList users={users} />}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}



// Snarky Anxiety Remedies for the Panic Modal
const PANIC_REMEDIES = [
  "Remember: you survived Foss Hill on 4/20. You can handle a delayed flight.",
  "Channel your inner Wes student: accept the chaos, call it 'an experimental process,' and move on.",
  "If the vibe is off, change the music. If the energy is low, find the nearest snack. If all else fails, blame the algorithms.",
  "It is 70 degrees and sunny in Puerto Rico. This is not a crisis. This is a pool.",
  "Whatever happens, we will survive. We are the friends who made it through finals week. We can handle anything.",
  "Panic is just your brain asking for a nap. Take one.",
  "Breathe in. Breathe out. Repeat until the urge to spiral passes.",
  "Take a second. You don't have to turn every minor setback into a profound learning experience. Just breathe.",
  "We're on island time now. The clock is merely a suggestion.",
  "Worst case scenario: we're stuck in Puerto Rico with each other. Honestly, not the worst outcome.",
  "Your itinerary is a guideline, not a legally binding document. Adjust as needed.",
  "Take a deep breath. You are currently paying money to relax, so let's try doing that.",
  "You're spiraling, but thankfully, you brought 7 people whose primary job is to tether you to reality.",
  "Before you panic, ask yourself: \"What would Tedra do?\" (Answer: probably order a snack and reassess.)",
  "Henry is somewhere nearby being entirely unbothered. Borrow some of that energy for a second.",
  "You survived 8am classes. You can survive this.",
  "This is temporary. This is survivable. This is vacation.",
  "Aleyda's official prognosis: You are totally fine, just slightly overwhelmed. Deep breath.",
  "Janet has a plan, Azayza has the vibes, and Caroline has the snacks. You are completely covered.",
  "You pressed the button, which means you get 60 seconds of dramatic venting, then we fix it.",
  "You're allowed to be stressed, but you're not allowed to pretend you're in this alone. Lean on your friends.",
  "Deep breath. We have 12 years of collective problem-solving power in this group. Use us.",
  "The only crisis here is how fast these chips are disappearing. Stay focused.",
  "Unclench your teeth, relax your forehead, and remember that we actually love you.",
  "Pressing Panic was step one. Step two is letting out a big sigh and letting us help.",
  "You’ve survived 100% of your worst days so far. Today isn't even going to make the top 50.",
  "You are in Puerto Rico. The sand is warm, the rum is cold, and whatever just happened in the group chat absolutely does not qualify as an emergency.",
  "Before you panic, confirm: are you within walking distance of the ocean? You are. Go stand in it. The problem will still be there when you get back, but you will care about it less.",
  "Aleyda has already scoped the situation, formed an opinion, and is prepared to deliver it with confidence. Find her. Let her talk. Feel better.",
  "It has been twelve years. You have all changed, grown, and become genuinely impressive people. You can absolutely figure out where to eat dinner.",
  "Aleyda did not fly all the way to Puerto Rico to deal with this energy. None of you did. Reset, regroup, and meet back at the pool in ten.",
  "Hailey and Wynn are somewhere on this island having a significantly better time than you are right now. This is data. Use it.",
  "Whatever the disagreement is, you have all survived finals week, post-graduation chaos, and twelve years of group chat chaos. This is nothing.",
  "Janet has seen this group at its absolute worst and still booked the flight. Trust that she knows something you don't, which is that it always works out.",
  "Henry is not panicking. Henry is never panicking. Ask Henry what he is doing and do that instead.",
  "Hailey is already problem-solving in the background. Let her finish. The situation will be resolved before you even finish reading this.",
  "You are adults. Accomplished, well-traveled, capable adults. Who cannot agree on a dinner reservation. In paradise. Incredible. Go get a piña colada and try again.",
  "If the group energy has gone sideways, someone needs to suggest the beach. Someone always needs to suggest the beach. Be that person.",
  "Twelve years ago you were broke, exhausted, and eating dining hall food together. You are now in Puerto Rico. Whatever this is, it is not a crisis. It is a character arc.",
  "If the itinerary has collapsed, good. The best Puerto Rico stories never came from the itinerary.",
  "You have a group chat, you have each other's numbers, and you have been coordinating chaos together for over a decade. You are going to be fine.",
  "Breathe. You made it here. All of you, from wherever life scattered you, made it to the same island at the same time. That is the whole point. Everything else is details.",
  "If the vibe has shifted, Wynn knows how to bring it back. This is a documented fact. Find Wynn.",
  "Somewhere on this island, a tree is growing a coconut that will eventually become a drink that would fix exactly what you are feeling right now. Honor that coconut. Go find its cousin at the bar.",
  "You are not lost. You are in Puerto Rico without a plan, which is technically the best possible version of being in Puerto Rico.",
  "The ocean does not care about the disagreement. The ocean is just there, being enormous and beautiful and free, as a reminder that some things are bigger than the thing you are currently upset about. Go look at it.",
  "This is an excellent group chat topic. Run it. Debate it. Solve it. Then order margaritas.",
  "You are allowed to need a minute. You are allowed to want a snack. You are allowed to be the person who just sits quietly until the chaos moves on to the next phase. Pick your lane and stay in it for thirty minutes.",
  "If the plan fell through, wonderful. That is now an anecdote. Congratulations. Go expand your repertoire of anecdotes.",
  "The purpose of this trip is to be together, preferably somewhere warm, preferably with drinks. The fact that this is currently not happening is temporary. The fact that you are together is permanent. Focus on the permanent thing.",
  "Take a mental snapshot. It is 70 degrees, you are in Puerto Rico, and you are surrounded by people who have known you for over a decade. Even if this exact moment is annoying, the context is perfect. Appreciate the context.",
  "Somewhere on this island, a bartender is mixing a drink that would make this entire situation feel manageable. You are very close to finding that bartender. Do not give up.",
  "You are currently experiencing 20% of the trip and 80% of the chaos. That is a 4-to-1 ratio that is completely backwards. Fix it with a walk to the beach.",
  "You are in Puerto Rico. The water is warm, the rum is cold, and your friends have been through significantly worse with you. Take a deep breath. This is not a crisis. This is a plot twist. Enjoy it.",
];

// Beautiful Summary Dashboard Overview
function DashboardView({ setActiveTab, users = [], onLogout }) {
  const userName = localStorage.getItem("dayones_user_name") || "Friend";

  const getUserName = (id) => {
    if (!id) return "";
    const foundById = users.find(u => String(u.id) === String(id));
    if (foundById) return foundById.name;
    const foundByName = users.find(u => u.name.toLowerCase().trim() === String(id).toLowerCase().trim());
    if (foundByName) return foundByName.name;
    return id;
  };

  const [allStatuses, setAllStatuses] = useState([]);
  const [myStatusText, setMyStatusText] = useState("");
  const [myStatusEmoji, setMyStatusEmoji] = useState("📍");
  const [showPanicModal, setShowPanicModal] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedDiscussionIds, setExpandedDiscussionIds] = useState({});
  const [allPanics, setAllPanics] = useState([]);
  const [selectedRemedy, setSelectedRemedy] = useState("");
  const [weather, setWeather] = useState({ current: null, high: null, low: null, loading: true });

  const getRelativeTimeString = (timestamp) => {
    if (!timestamp) return "Active Now";
    const diffMs = Date.now() - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins === 1) return "1 minute ago";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  useEffect(() => {
    const unsub = subscribeToCollection("panics", (data) => {
      setAllPanics(data || []);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeToCollection("statuses", (data) => {
      setAllStatuses(data);
    }, []);
    return unsub;
  }, []);

  // Fetch real weather forecast from Open-Meteo for Kite Beach / Las Picuas, Rio Grande, PR coordinate (lat: 18.4278, lon: -65.7706)
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=18.4278&longitude=-65.7706&current=temperature_2m&daily=temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&timezone=America%2FPuerto_Rico"
        );
        if (response.ok) {
          const data = await response.json();
          setWeather({
            current: Math.round(data.current.temperature_2m),
            high: Math.round(data.daily.temperature_2m_max[0]),
            low: Math.round(data.daily.temperature_2m_min[0]),
            loading: false
          });
        } else {
          setWeather(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        console.error("Error fetching live weather:", err);
        setWeather(prev => ({ ...prev, loading: false }));
      }
    };
    fetchWeather();
  }, []);

  const currentMyStatus = allStatuses
    .filter(s => s.name === userName || s.id === userName)
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
  const currentMyStatusEmoji = currentMyStatus ? currentMyStatus.emoji : null;

  useEffect(() => {
    if (currentMyStatusEmoji) {
      setMyStatusEmoji(currentMyStatusEmoji);
    }
  }, [currentMyStatusEmoji]);



  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!myStatusText.trim()) return;
    const statusId = `status_${userName}_${Date.now()}`;
    await saveDocument("statuses", statusId, {
      name: userName,
      status: myStatusText.trim(),
      emoji: myStatusEmoji || "📍",
      timestamp: Date.now()
    });
    setMyStatusText("");
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm("Are you sure you want to delete this status?")) return;
    await deleteDocument("statuses", statusId);
  };

  const handleStatusLike = async (person) => {
    if (!userName || userName === "Friend") return;
    const likedBy = Array.isArray(person.likedBy) ? person.likedBy : [];
    const alreadyLiked = likedBy.includes(userName);
    const newLikedBy = alreadyLiked
      ? likedBy.filter(u => u !== userName)
      : [...likedBy, userName];
    await saveDocument("statuses", person.id, {
      ...person,
      likedBy: newLikedBy,
      likes: newLikedBy.length
    });
  };

  const handleAddStatusComment = async (person) => {
    const text = (commentInputs[person.id] || "").trim();
    if (!text || !userName || userName === "Friend") return;
    const existing = Array.isArray(person.comments) ? person.comments : [];
    const newComment = {
      id: `c_${Date.now()}`,
      user: userName,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      text
    };
    await saveDocument("statuses", person.id, {
      ...person,
      comments: [...existing, newComment]
    });
    setCommentInputs(prev => ({ ...prev, [person.id]: "" }));
  };

  const handleDeleteStatusComment = async (person, commentId) => {
    const existing = Array.isArray(person.comments) ? person.comments : [];
    await saveDocument("statuses", person.id, {
      ...person,
      comments: existing.filter(c => c.id !== commentId)
    });
  };

  const handleOpenPanic = async () => {
    const randomIndex = Math.floor(Math.random() * PANIC_REMEDIES.length);
    setSelectedRemedy(PANIC_REMEDIES[randomIndex]);
    setShowPanicModal(true);

    // Get formatted local date string YYYY-MM-DD
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    // Look up count for today for this user
    const myTodayDoc = allPanics.find(p => p.date === todayStr && p.user === userName);
    const currentCount = myTodayDoc ? (myTodayDoc.count || 0) : 0;

    // Save document keyed by date and username to allow aggregate summation and user totals
    const docId = `${todayStr}_${userName}`;
    await saveDocument("panics", docId, {
      date: todayStr,
      user: userName,
      count: currentCount + 1
    });
  };

  const totalPanicsToday = (() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    return allPanics
      .filter(p => p.date === todayStr)
      .reduce((sum, item) => sum + (item.count || 0), 0);
  })();


  const { daysUntil, isVacationWeek, isPastTrip } = getVacationCountdown();

  return (
    <div className="tab-panel">
      {/* Light Mode overrides for Panic Modal */}
      <style dangerouslySetInnerHTML={{
        __html: `
        :root[data-theme="light"] .panic-overlay {
          background: rgba(192, 57, 43, 0.20) !important;
        }
        :root[data-theme="light"] .panic-content {
          background: #fcf9f5 !important;
          border-color: rgba(192, 57, 43, 0.25) !important;
          box-shadow: 0 20px 50px rgba(192, 57, 43, 0.15) !important;
          color: #1a1a1a !important;
        }
        :root[data-theme="light"] .panic-content h3 {
          color: #b3261e !important;
        }
        :root[data-theme="light"] .panic-content .modal-close {
          color: #6b6559 !important;
        }
        :root[data-theme="light"] .panic-content .modal-close:hover {
          color: #1a1a1a !important;
        }
        :root[data-theme="light"] .panic-title {
          color: #b3261e !important;
        }
        :root[data-theme="light"] .panic-remedy-box {
          background: rgba(192, 57, 43, 0.03) !important;
          border-color: rgba(192, 57, 43, 0.2) !important;
          color: #7a1f16 !important;
        }
      `}} />

      {/* Console main dashboard banner */}
      <div className="console-dashboard-header" style={{ marginBottom: '16px', paddingBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-title)', fontSize: '2.5rem', fontWeight: '800', letterSpacing: '-1px', margin: '0 0 8px 0' }}>
            <span style={{ fontStyle: 'italic', color: 'var(--color-info)', textShadow: '2px 2px 0px var(--color-heading-shadow)' }}>Welcome back,</span> <span className="welcome-username">{getUserName(userName)}!</span>
          </h2>
        </div>

        {/* Vacation countdown — swaps to "Vacation Mode" for the duration of the trip week */}
        {!isPastTrip && (
          <div className={`vacation-countdown-banner ${isVacationWeek ? 'is-vacation-mode' : ''}`}>
            {isVacationWeek ? (
              "🌴 Vacation Mode"
            ) : (
              <>
                <span className="vacation-countdown-spinner">⏳</span> {daysUntil} {daysUntil === 1 ? "Day" : "Days"} Until Vacation
              </>
            )}
          </div>
        )}
      </div>

      {/* Weather, Spotify Playlist, and Photos Album as a hairline-divided quick-links row */}
      <div className="quick-links-row">
        <a
          href="https://weather.com/pr/city/san-juan/today"
          target="_blank"
          rel="noopener noreferrer"
          className="quick-link-chip"
        >
          <span className="quick-link-icon">☀️</span>
          {weather.loading ? (
            "Loading..."
          ) : (
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
              <span>{weather.current !== null ? weather.current : 76}°F in San Juan</span>
              <span style={{ fontSize: '0.62rem', opacity: 0.7, letterSpacing: '0.04em' }}>
                H:{weather.high !== null ? weather.high : '--'}&deg; L:{weather.low !== null ? weather.low : '--'}&deg;
              </span>
            </span>
          )}
        </a>
        <a
          href="https://open.spotify.com/playlist/0e0qISTyQSQD2meUSAm9vx?si=cb4c057556454bbf&pt=7ec1018c2d5d0ae79960635b8388f1c5"
          target="_blank"
          rel="noopener noreferrer"
          className="quick-link-chip"
        >
          <span className="quick-link-icon">🎵</span>
          Playlist
        </a>
        <a
          href="https://photos.app.goo.gl/TU5RSb5BfEjp6NpZA"
          target="_blank"
          rel="noopener noreferrer"
          className="quick-link-chip"
        >
          <span className="quick-link-icon">📸</span>
          Photos
        </a>
        <a
          href="https://docs.google.com/spreadsheets/d/1JA-OaA47pwYWLrBx-Y-S4SouWaK7QQbDKTGe-x3eWxU/edit?gid=818135796#gid=818135796"
          target="_blank"
          rel="noopener noreferrer"
          className="quick-link-chip"
        >
          <span className="quick-link-icon">💡</span>
          <span style={{ display: 'inline-block', maxWidth: '88px', textAlign: 'left' }}>Planning Spreadsheet</span>
        </a>
      </div>

      {/* Unified Status Board */}
      <div className="dashboard-secondary-grid" style={{ marginBottom: '32px' }}>
        <div className="glass-panel" style={{ padding: '24px', position: 'relative' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>📍 Status Board</span>
          </h3>
          <div className="status-board-content">
            {/* Left Column: Update Status Form */}
            <div className="status-board-left">
              <h4 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', marginBottom: '12px' }}>Your Status</h4>
              {currentMyStatus ? (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(var(--color-primary-rgb), 0.05)', borderRadius: '0px', borderLeft: '3px solid var(--color-primary)' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--color-primary)', display: 'block', marginBottom: '4px' }}>Current Status</span>
                  <p style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--color-text-main)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.2rem' }}>{currentMyStatus.emoji || "📍"}</span>
                    {currentMyStatus.status}
                  </p>
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: '4px', display: 'block' }}>Updated {getRelativeTimeString(currentMyStatus.timestamp)}</span>
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: '1.5', marginBottom: '16px' }}>
                  Let your friends know where you are or what you're up to (e.g. napping, pool deck, hiking).
                </p>
              )}

              <form onSubmit={handleUpdateStatus} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    maxLength={1}
                    value={myStatusEmoji}
                    onChange={e => setMyStatusEmoji(e.target.value.slice(0, 1))}
                    style={{
                      width: '50px',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                      borderRadius: '0px',
                      background: 'rgba(252, 249, 245, 0.05)',
                      border: '2px solid var(--color-border-strong)',
                      color: 'var(--color-text-main)',
                      fontFamily: 'var(--font-body)',
                      outline: 'none'
                    }}
                    placeholder="📍"
                    title="Custom status emoji"
                  />
                  <input
                    type="text"
                    placeholder="e.g. Sorting puzzle pieces"
                    className="input-field"
                    style={{ flex: 1, fontSize: '0.85rem', padding: '10px 12px', minWidth: 0, borderRadius: '0px' }}
                    value={myStatusText}
                    onChange={e => setMyStatusText(e.target.value)}
                    required
                  />
                </div>

                {/* Predefined Quick Emoji Chips */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                  {["📍", "🔥", "🏊‍♂️", "🍔", "💭", "💛", "🕶️", "🎲", "🍿", "🪩", "📖", "😴"].map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setMyStatusEmoji(emoji)}
                      style={{
                        background: myStatusEmoji === emoji ? 'rgba(var(--color-primary-rgb), 0.15)' : 'rgba(252, 249, 245, 0.03)',
                        border: `2px solid ${myStatusEmoji === emoji ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
                        borderRadius: '9999px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s ease',
                        fontFamily: 'var(--font-body)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                <button type="submit" className="btn-primary" style={{ fontSize: '0.8rem', padding: '8px 12px', justifyContent: 'center' }}>
                  Update Status
                </button>
              </form>
            </div>

            {/* Right Column: Status Feed */}
            <div className="status-board-right" style={{ display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontFamily: 'var(--font-title)', fontSize: '1rem', color: 'var(--color-text-main)', marginBottom: '16px' }}>Status Feed</h4>
              {allStatuses.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: 'auto' }}>
                  No one has checked in yet. Be the first to update your status!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                  {[...allStatuses].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).map(person => {
                    const likedBy = Array.isArray(person.likedBy) ? person.likedBy : [];
                    const hasLiked = likedBy.includes(userName);
                    const comments = Array.isArray(person.comments) ? person.comments : [];
                    const isStatusExpanded = expandedDiscussionIds[person.id] !== undefined
                      ? expandedDiscussionIds[person.id]
                      : comments.length > 0;
                    return (
                      <div
                        key={person.id}
                        className="glass-panel"
                        style={{
                          background: 'rgba(252, 249, 245, 0.02)',
                          border: '1px solid var(--color-glass-border)',
                          borderRadius: '0px',
                          padding: '12px 14px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          boxShadow: 'none',
                          transition: 'all 0.2s ease',
                          margin: 0
                        }}
                      >
                        {/* Header: name + time + delete */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <span style={{ fontWeight: '700', color: 'var(--color-primary)', fontSize: '0.8rem' }}>
                            {person.emoji || "📍"} {getUserName(person.name || person.id)}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>
                              {getRelativeTimeString(person.timestamp)}
                            </span>
                            {(person.name === userName || person.id === userName) && (
                              <button
                                onClick={() => handleDeleteStatus(person.id)}
                                className="btn-inline-delete-cross"
                                style={{ lineHeight: 1 }}
                                title="Delete Status"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Status text */}
                        <p style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-text-main)', margin: 0, lineHeight: '1.4' }}>
                          <span style={{ wordBreak: 'break-word' }}>{person.status}</span>
                        </p>

                        {/* Actions Row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <VoteButton
                            votes={likedBy.length}
                            hasVoted={hasLiked}
                            onVote={() => handleStatusLike(person)}
                            votedBy={likedBy}
                            users={users}
                            itemType="status"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDiscussionIds(prev => ({
                                ...prev,
                                [person.id]: !isStatusExpanded
                              }));
                            }}
                            style={{
                              background: isStatusExpanded ? 'rgba(var(--color-primary-rgb), 0.12)' : 'rgba(252, 249, 245, 0.03)',
                              border: isStatusExpanded ? '1px solid rgba(var(--color-primary-rgb), 0.3)' : '1px solid rgba(252, 249, 245, 0.08)',
                              borderRadius: '0px',
                              padding: '4px 10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              color: isStatusExpanded ? 'var(--color-primary)' : 'var(--color-text-muted)',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              fontFamily: 'var(--font-body)'
                            }}
                            title="Reply / Discussion"
                          >
                            💬 {comments.length}
                          </button>
                        </div>

                        {/* Discussion thread */}
                        <DiscussionThread
                          comments={comments}
                          commentInput={commentInputs[person.id] || ""}
                          onCommentInputChange={(val) => setCommentInputs(prev => ({ ...prev, [person.id]: val }))}
                          onAddComment={() => handleAddStatusComment(person)}
                          onDeleteComment={(commentId) => handleDeleteStatusComment(person, commentId)}
                          placeholder="Reply to this status..."
                          users={users}
                          isExpanded={isStatusExpanded}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Polls */}
      <PollBoard users={users} />

      {/* Dashboard footer actions: panic + logout, out of the nav sidebar */}
      <div className="dashboard-footer-actions">
        <button
          onClick={handleOpenPanic}
          className="btn-panic-large"
          title="Panic"
          aria-label="Panic"
        >
          🚨 Panic 🚨
        </button>
        <button
          onClick={onLogout}
          className="btn-logout-small"
          title="Log Out"
          aria-label="Log Out"
        >
          <Lock size={14} strokeWidth={1.75} />
          Log Out
        </button>
      </div>

      {/* High-fidelity Panic Overlay Modal */}
      <Lightbox
        isOpen={showPanicModal}
        onClose={() => setShowPanicModal(false)}
        title={
          <span style={{ color: 'var(--color-danger)', fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🚨 Minor Panic Management
          </span>
        }
        maxWidth="520px"
        overlayClassName="panic-overlay"
        contentClassName="panic-content"
        headerStyle={{ borderColor: 'rgba(192, 57, 43, 0.15)', padding: '16px 20px' }}
      >
        <div className="modal-body panic-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          <div className="panic-title" style={{ fontSize: '2.5rem', margin: 0, fontWeight: '800' }}>CHILLAX! 🍹</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', fontWeight: '700' }}>
            {totalPanicsToday > 0
              ? `Friends have panicked ${totalPanicsToday} ${totalPanicsToday === 1 ? 'time' : 'times'} today. Stay strong!`
              : "No panic events logged today yet."}
          </div>
          <div
            className="panic-remedy-box"
            style={{
              width: '100%',
              background: 'rgba(192, 57, 43, 0.03)',
              border: '2px solid rgba(192, 57, 43, 0.3)',
              boxShadow: 'none',
              padding: '16px',
              borderRadius: '0px',
              fontSize: '1rem',
              color: 'var(--color-text-main)',
              lineHeight: '1.5',
              textAlign: 'left',
              fontWeight: '600',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '3rem', opacity: 0.05, pointerEvents: 'none' }}>🧘‍♀️</div>
            {selectedRemedy || "Put down your phone, grab a towel, apply SPF 50, jump into the luxury villa pool, and take a long breath. Puerto Rico is wonderful."}
          </div>

          <button
            className="btn-primary"
            style={{
              background: 'linear-gradient(135deg, var(--color-danger), #b3261e)',
              borderColor: 'var(--color-border-strong)',
              marginTop: '8px',
              padding: '10px 24px',
              fontSize: '0.85rem',
              cursor: 'pointer',
              fontWeight: '700',
              fontFamily: 'var(--font-body)'
            }}
            onClick={() => setShowPanicModal(false)}
          >
            I Am Calmer Now, Stop Panicking
          </button>
        </div>
      </Lightbox>
    </div>
  );
}

export default App;
